// ============================================================================
// CONFIG SETTING FIELD
// ============================================================================
// Renders a single configuration setting with the appropriate control
// based on its dataType (Boolean, Integer, Decimal, String, List).
//
// DROPDOWN_KEYS: maps specific configKey values to a static list of options.
// Extend this map with any settings that should render as a select dropdown
// instead of a plain text input.

import React, { useMemo, useCallback } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import ConfigListEditor from './ConfigListEditor';
import type {
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types/config.types';
import type { ScopeType } from 'hooks/api/useConfigSettings';

interface ConfigSettingFieldProps {
  definition: ConfigDefinition;
  effectiveValue: ConfigEffectiveValue;
  editedValue?: ConfigValueUpdate;
  onValueChange: (configKey: string, value?: string, listValues?: ConfigListItem[]) => void;
  onResetValue: (configKey: string) => void;
  scopeType: ScopeType;
}

// Keys that should render as SyutSelect dropdowns instead of plain inputs.
// Add your domain-specific dropdown settings here.
const DROPDOWN_KEYS: Record<string, SelectOption[]> = {
  date_format: [
    { value: 'DD/MM/YYYY',   label: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY',   label: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD',   label: 'YYYY-MM-DD (ISO 8601)' },
    { value: 'DD-MMM-YYYY',  label: 'DD-MMM-YYYY' },
  ],
  timezone: [
    { value: 'UTC',                  label: 'UTC' },
    { value: 'America/New_York',     label: 'Eastern Time (ET)' },
    { value: 'America/Chicago',      label: 'Central Time (CT)' },
    { value: 'America/Los_Angeles',  label: 'Pacific Time (PT)' },
    { value: 'Europe/London',        label: 'London (GMT/BST)' },
    { value: 'Europe/Paris',         label: 'Central European Time (CET)' },
    { value: 'Asia/Kolkata',         label: 'India Standard Time (IST)' },
    { value: 'Asia/Tokyo',           label: 'Japan Standard Time (JST)' },
    { value: 'Australia/Sydney',     label: 'Australian Eastern Time (AEST)' },
  ],
};

const ConfigSettingField: React.FC<ConfigSettingFieldProps> = ({
  definition,
  effectiveValue,
  editedValue,
  onValueChange,
  onResetValue,
  scopeType,
}) => {
  const isSystemOnly  = definition.maxScopeLevel === 0;
  const isOrgMax      = definition.maxScopeLevel <= 1;

  // Read-only when scope exceeds what this setting allows
  const isReadOnly =
    (isSystemOnly && scopeType !== 'SYSTEM') ||
    (isOrgMax && scopeType === 'DEPARTMENT');

  // Badge shown for non-SYSTEM scopes
  const badge = useMemo(() => {
    if (scopeType === 'SYSTEM') return null;

    if (isSystemOnly) {
      return { className: 'config-badge-system-only', label: 'System Only' };
    }
    if (scopeType === 'DEPARTMENT' && isOrgMax) {
      return { className: 'config-badge-system-only', label: 'Org Max' };
    }

    const isOverridden =
      effectiveValue.resolvedFromScopeType === scopeType ||
      (editedValue && editedValue.value !== undefined);

    return isOverridden
      ? { className: 'config-badge-override',   label: 'Override' }
      : { className: 'config-badge-inherited',  label: 'Inherited' };
  }, [isSystemOnly, isOrgMax, scopeType, effectiveValue, editedValue]);

  const currentValue = useMemo(() => {
    if (editedValue?.value !== undefined) return editedValue.value;
    return effectiveValue.effectiveValue ?? definition.defaultValue ?? '';
  }, [editedValue, effectiveValue, definition]);

  const currentListValues = useMemo(() => {
    if (editedValue?.listValues) return editedValue.listValues;
    return effectiveValue.listValues ?? [];
  }, [editedValue, effectiveValue]);

  const originalListValues = useMemo(
    () => new Set((effectiveValue.listValues ?? []).map((i) => i.value)),
    [effectiveValue]
  );

  // Show Reset only when a scope-level override exists and can be cleared
  const canReset =
    scopeType !== 'SYSTEM' &&
    !isReadOnly &&
    (effectiveValue.resolvedFromScopeType === scopeType || editedValue !== undefined);

  const handleChange = useCallback(
    (value: string) => onValueChange(definition.configKey, value),
    [definition.configKey, onValueChange]
  );

  const handleListChange = useCallback(
    (listValues: ConfigListItem[]) => onValueChange(definition.configKey, undefined, listValues),
    [definition.configKey, onValueChange]
  );

  const handleReset = useCallback(
    () => onResetValue(definition.configKey),
    [definition.configKey, onResetValue]
  );

  const renderControl = () => {
    // Static dropdown overrides
    const dropdownOptions = DROPDOWN_KEYS[definition.configKey];
    if (dropdownOptions) {
      return (
        <div style={{ minWidth: '220px' }}>
          <SyutSelect
            value={currentValue}
            onChange={(value: string | string[] | null) =>
              handleChange(typeof value === 'string' ? value : '')
            }
            options={dropdownOptions}
            isDisabled={isReadOnly}
            size="sm"
          />
        </div>
      );
    }

    switch (definition.dataType) {
      case 'Boolean':
        return (
          <label className="config-toggle-switch">
            <input
              type="checkbox"
              checked={currentValue === 'true' || currentValue === 'True'}
              onChange={(e) => handleChange(e.target.checked ? 'true' : 'false')}
              disabled={isReadOnly}
            />
            <span className="config-toggle-slider" />
          </label>
        );

      case 'Integer':
        return (
          <input
            className="config-number-input"
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseInt(definition.minValue, 10) : undefined}
            max={definition.maxValue ? parseInt(definition.maxValue, 10) : undefined}
            step={1}
            disabled={isReadOnly}
          />
        );

      case 'Decimal':
        return (
          <input
            className="config-number-input"
            type="number"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            min={definition.minValue ? parseFloat(definition.minValue) : undefined}
            max={definition.maxValue ? parseFloat(definition.maxValue) : undefined}
            step={0.01}
            disabled={isReadOnly}
          />
        );

      case 'List':
        return (
          <ConfigListEditor
            values={currentListValues}
            onChange={handleListChange}
            originalValues={originalListValues}
            unitLabel={definition.unitLabel}
            isReadOnly={isReadOnly}
          />
        );

      default: // String
        return (
          <input
            className="config-text-input"
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            disabled={isReadOnly}
          />
        );
    }
  };

  return (
    <div
      className={`config-setting-row${
        definition.dataType === 'List' ? ' config-setting-row--list' : ''
      }`}
    >
      <div className="config-setting-info">
        <div className="config-setting-label">
          <span>{definition.displayName}</span>
          {definition.unitLabel && (
            <span className="config-setting-unit">({definition.unitLabel})</span>
          )}
          {badge && (
            <span className={`config-badge ${badge.className}`}>{badge.label}</span>
          )}
        </div>
        {definition.description && (
          <div className="config-setting-description">{definition.description}</div>
        )}
      </div>
      <div className="config-setting-control">
        {renderControl()}
        {canReset && (
          <button
            type="button"
            className="config-reset-btn"
            onClick={handleReset}
            title="Reset to inherited / system default"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfigSettingField;
